import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { testimonials } from "../lib/data";
import { ChevronLeft, ChevronRight, Quote } from "lucide-react";

const Testimonials = () => {
  const [i, setI] = useState(0);
  const current = testimonials[i];

  const next = () => setI((i + 1) % testimonials.length);
  const prev = () => setI((i - 1 + testimonials.length) % testimonials.length);

  return (
    <section className="py-24 md:py-36 relative overflow-hidden" data-testid="testimonials">
      <div className="mx-auto max-w-[1600px] px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-14 md:mb-20 gap-6">
          <div>
            <div className="section-label mb-6">© Testimonials</div>
            <h2 className="h-display text-5xl md:text-7xl lg:text-8xl">
              Real <span className="italic font-serif">Feedback.</span>
            </h2>
          </div>
          <div className="flex items-center gap-3">
            <button
              aria-label="Previous testimonial"
              onClick={prev}
              data-cursor="hover"
              data-testid="testimonial-prev"
              className="w-12 h-12 rounded-full border border-[color:var(--nn-border)] grid place-items-center hover:border-[color:var(--nn-accent)] transition-colors"
            >
              <ChevronLeft size={18} />
            </button>
            <span className="font-mono text-xs text-[color:var(--nn-muted)]">
              {String(i + 1).padStart(2, "0")} / {String(testimonials.length).padStart(2, "0")}
            </span>
            <button
              aria-label="Next testimonial"
              onClick={next}
              data-cursor="hover"
              data-testid="testimonial-next"
              className="w-12 h-12 rounded-full border border-[color:var(--nn-border)] grid place-items-center hover:border-[color:var(--nn-accent)] transition-colors"
            >
              <ChevronRight size={18} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-14 items-center">
          <div className="md:col-span-4">
            <AnimatePresence mode="wait">
              <motion.div
                key={current.author}
                initial={{ opacity: 0, y: 20, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.98 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="relative aspect-square rounded-2xl overflow-hidden border border-[color:var(--nn-border)]"
              >
                <img src={current.avatar} alt={current.author} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                <div className="absolute bottom-5 left-5 text-white">
                  <div className="font-serif text-2xl">{current.author}</div>
                  <div className="text-xs opacity-80 mt-0.5">{current.title}</div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="md:col-span-8">
            <Quote className="text-[color:var(--nn-accent)] mb-6" size={40} />
            <AnimatePresence mode="wait">
              <motion.blockquote
                key={current.author + "-q"}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="font-serif text-2xl md:text-4xl lg:text-5xl leading-tight tracking-tight text-[color:var(--nn-text)]"
              >
                {current.quote}
              </motion.blockquote>
            </AnimatePresence>
          </div>
        </div>

        <div className="mt-14 flex gap-2">
          {testimonials.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setI(idx)}
              aria-label={`Show testimonial ${idx + 1}`}
              data-cursor="hover"
              className={`h-[2px] flex-1 max-w-[80px] transition-colors ${
                idx === i ? "bg-[color:var(--nn-accent)]" : "bg-[color:var(--nn-border)]"
              }`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
